import TextFieldComponent from '../../../components/TextFieldComponent';
import CheckboxComponent from '../../../components/CheckboxComponent';
import ButtonComponent from '../../../components/ButtonComponent';
import { Grid2 } from '@mui/material';
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import zod from 'zod';
import { DeviceNtpConfigFormSchema } from '../../../services/Devices/schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useGetNtpConfigAsync,
  useGetTimeConfigAsync,
  useUpdateNtpConfigAsync,
  useUpdateTimeConfigAsync,
} from '../../../services/Devices/hooks/useMutation';
import { AppContext } from '../../../context/AppContextProvider';
import { NumberFieldComponent } from '../../../components/NumberFieldComponent';
import DropSelectComponent, {
  IDropSelectOptions,
} from '../../../components/DropSelectComponent';
import { setValue } from 'reselect/src/autotrackMemoize/autotracking';
import TimerComponent from '../../../components/TimerComponent/TimerComponent';

interface DeviceNtpConfigProps {
  id: number;
  setIsPendingChanges: Dispatch<SetStateAction<boolean>>;
  setFunctionToConfirmPendingChanges: Dispatch<
    SetStateAction<() => Promise<void>>
  >;
}

type DeviceNtpConfigFormInput = zod.infer<typeof DeviceNtpConfigFormSchema>;

const DeviceNtpConfig = ({
  id,
  setIsPendingChanges,
  setFunctionToConfirmPendingChanges,
}: DeviceNtpConfigProps) => {
  const { showSnackbar } = useContext(AppContext);
  const [time, setTime] = useState<Date>(new Date());
  const { control, reset, handleSubmit, setValue } =
    useForm<DeviceNtpConfigFormInput>({
      resolver: zodResolver(DeviceNtpConfigFormSchema),
      defaultValues: {
        address: '',
        isEnabled: false,
        port: 0,
        updatePeriodMinutes: 0,
      },
    });

  const {
    mutateAsync: mutateGetNtpConfigAsync,
    isLoading: isLoadingGetNtpConfigAsync,
  } = useGetNtpConfigAsync();

  const {
    mutateAsync: mutateUpdateNtpConfigAsync,
    isLoading: isLoadingUpdateNtpConfigAsync,
  } = useUpdateNtpConfigAsync();

  const {
    mutateAsync: mutateGetTimeConfigAsync,
    isLoading: isLoadingGetTimeConfigAsync,
  } = useGetTimeConfigAsync();

  const {
    mutateAsync: mutateUpdateTimeConfigAsync,
    isLoading: isLoadingUpdateTimeConfigAsync,
  } = useUpdateTimeConfigAsync();

  const timeZoneOptions: IDropSelectOptions[] = [
    {
      option: '(UTC-12:00) Linha de Data Internacional Oeste',
      value: 'ZONE_32',
      description: 'Linha de Data Internacional Oeste',
    },
    {
      option: '(UTC-11:00) Tempo Universal Coordenado -11',
      value: 'ZONE_31',
      description: 'Tempo Universal Coordenado -11',
    },
    { option: '(UTC-10:00) Havaí', value: 'ZONE_30', description: 'Havaí' },
    {
      option: '(UTC-09:30) Ilhas Marquesas',
      value: 'ZONE_36',
      description: 'Ilhas Marquesas',
    },
    { option: '(UTC-09:00) Alasca', value: 'ZONE_29', description: 'Alasca' },
    {
      option: '(UTC-08:00) Baixa Califórnia',
      value: 'ZONE_28',
      description: 'Baixa Califórnia',
    },
    { option: '(UTC-07:00) Arizona', value: 'ZONE_27', description: 'Arizona' },
    {
      option: '(UTC-06:00) América Central',
      value: 'ZONE_26',
      description: 'América Central',
    },
    {
      option: '(UTC-05:00) Hora do Leste (EUA e Canadá)',
      value: 'ZONE_25',
      description: 'Hora do Leste (EUA e Canadá)',
    },
    {
      option: '(UTC-04:00) Georgetown, La Paz, Manaus, San Juan',
      value: 'ZONE_24',
      description: 'Georgetown, La Paz, Manaus, San Juan',
    },
    {
      option: '(UTC-03:30) Terra Nova',
      value: 'ZONE_23',
      description: 'Terra Nova',
    },
    { option: '(UTC-03:00) Brasil', value: 'ZONE_22', description: 'Brasil' },
    {
      option: '(UTC-02:00) Atlântico Central',
      value: 'ZONE_21',
      description: 'Atlântico Central',
    },
    { option: '(UTC-01:00) Açores', value: 'ZONE_20', description: 'Açores' },
    {
      option: '(UTC+00:00) Lisboa, Londres',
      value: 'ZONE_0',
      description: 'Lisboa, Londres',
    },
    {
      option: '(UTC+01:00) Berlim, Paris, Roma, Madrid',
      value: 'ZONE_1',
      description: 'Berlim, Paris, Roma, Madrid',
    },
    {
      option: '(UTC+02:00) Atenas, Jerusalém, Cairo',
      value: 'ZONE_2',
      description: 'Atenas, Jerusalém, Cairo',
    },
    {
      option: '(UTC+03:00) Moscou, São Petersburgo',
      value: 'ZONE_3',
      description: 'Moscou, São Petersburgo',
    },
    { option: '(UTC+03:30) Teerã', value: 'ZONE_4', description: 'Teerã' },
    {
      option: '(UTC+04:00) Abu Dhabi, Muscat',
      value: 'ZONE_5',
      description: 'Abu Dhabi, Muscat',
    },
    { option: '(UTC+04:30) Cabul', value: 'ZONE_6', description: 'Cabul' },
    {
      option: '(UTC+05:00) Islamabad, Carachi',
      value: 'ZONE_7',
      description: 'Islamabad, Carachi',
    },
    {
      option: '(UTC+05:30) Nova Deli, Mumbai, Kolkata',
      value: 'ZONE_8',
      description: 'Nova Deli, Mumbai, Kolkata',
    },
    {
      option: '(UTC+05:45) Kathmandu',
      value: 'ZONE_9',
      description: 'Kathmandu',
    },
    {
      option: '(UTC+06:00) Astana, Daca',
      value: 'ZONE_10',
      description: 'Astana, Daca',
    },
    {
      option: '(UTC+06:30) Yangon (Rangoon)',
      value: 'ZONE_11',
      description: 'Yangon (Rangoon)',
    },
    {
      option: '(UTC+07:00) Banguecoque, Hanói, Jacarta',
      value: 'ZONE_12',
      description: 'Banguecoque, Hanói, Jacarta',
    },
    {
      option: '(UTC+08:00) Pequim, Chongqing, Hong Kong, Urumqi',
      value: 'ZONE_13',
      description: 'Pequim, Chongqing, Hong Kong, Urumqi',
    },
    {
      option: '(UTC+08:30) Pyongyang',
      value: 'ZONE_37',
      description: 'Pyongyang',
    },
    { option: '(UTC+08:45) Eucla', value: 'ZONE_38', description: 'Eucla' },
    {
      option: '(UTC+09:00) Tóquio, Seul',
      value: 'ZONE_14',
      description: 'Tóquio, Seul',
    },
    {
      option: '(UTC+09:30) Adelaide, Darwin',
      value: 'ZONE_15',
      description: 'Adelaide, Darwin',
    },
    {
      option: '(UTC+10:00) Sydney, Melbourne, Canberra',
      value: 'ZONE_16',
      description: 'Sydney, Melbourne, Canberra',
    },
    {
      option: '(UTC+10:30) Ilha Lord Howe',
      value: 'ZONE_34',
      description: 'Ilha Lord Howe',
    },
    {
      option: '(UTC+11:00) Magadan, Ilhas Salomão, Nova Caledônia',
      value: 'ZONE_17',
      description: 'Magadan, Ilhas Salomão, Nova Caledônia',
    },
    {
      option: '(UTC+12:00) Fiji, Kamchatka, Auckland',
      value: 'ZONE_18',
      description: 'Fiji, Kamchatka, Auckland',
    },
    {
      option: '(UTC+12:45) Ilhas Chatham',
      value: 'ZONE_39',
      description: 'Ilhas Chatham',
    },
    {
      option: '(UTC+13:00) Tonga, Samoa',
      value: 'ZONE_19',
      description: 'Tonga, Samoa',
    },
    {
      option: '(UTC+14:00) Ilha Kiritimati',
      value: 'ZONE_35',
      description: 'Ilha Kiritimati',
    },
  ];

  const handleSave = (data: DeviceNtpConfigFormInput) => {
    mutateUpdateNtpConfigAsync(
      { id, data },
      {
        onSuccess: () => {
          showSnackbar('Configuração NTP atualizada com sucesso.', 'success');
          setIsPendingChanges(false);
        },
      },
    );
  };

  useEffect(() => {
    if (!id) return;

    mutateGetNtpConfigAsync(id).then((vl) => {
      reset(vl);
    });
    mutateGetTimeConfigAsync(id).then((vl) => {
      setTime(new Date(vl));
    });
  }, [id]);

  return (
    <>
      <Grid2 container columnSpacing={2} rowSpacing={2}>
        <Grid2 size={{ xl: 6, xs: 12 }}>
          <Controller
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <DropSelectComponent
                label={'Fuso horário'}
                options={timeZoneOptions}
                errorMessage={error?.message}
                valueSelected={value}
                isRequired
                onChange={(val) => {
                  onChange(val);
                  const zone = timeZoneOptions.find((v) => v.value === val);
                  if (zone && zone.description)
                    setValue('timeZoneDesc', zone.description);
                }}
              />
            )}
            name={'timeZone'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xl: 6, xs: 12 }}>
          <Controller
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextFieldComponent
                errorMessage={error?.message}
                label="Endereço"
                value={value}
                onChangeText={onChange}
              />
            )}
            name={'address'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xl: 6, xs: 12 }}>
          <Controller
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <NumberFieldComponent
                errorMessage={error?.message}
                label="Porta"
                value={value}
                onValueChange={onChange}
              />
            )}
            name={'port'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xl: 6, xs: 12 }}>
          <Controller
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <NumberFieldComponent
                errorMessage={error?.message}
                label="Período de atualização (em minutos)"
                value={value}
                onValueChange={onChange}
              />
            )}
            name={'updatePeriodMinutes'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xl: 6, xs: 12 }}>
          <TimerComponent startTime={time} />
        </Grid2>
        <Grid2 size={{ xl: 6, xs: 12 }} paddingLeft={2}>
          <Controller
            render={({ field: { onChange, value } }) => (
              <CheckboxComponent
                label="Sincronizar com NTP"
                value={value}
                onChange={onChange}
              />
            )}
            name={'isEnabled'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ mt: 1 }}>
          <ButtonComponent
            fullWidth
            onClick={handleSubmit(handleSave)}
            loading={
              isLoadingGetNtpConfigAsync ||
              isLoadingUpdateNtpConfigAsync ||
              isLoadingUpdateTimeConfigAsync ||
              isLoadingGetTimeConfigAsync
            }
          >
            Salvar
          </ButtonComponent>
        </Grid2>
      </Grid2>
    </>
  );
};

export default DeviceNtpConfig;
